import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './ContractionTimer.css';

function ContractionTimer({ userId }) {
  const [activeTimer, setActiveTimer] = useState(null);
  const [contractions, setContractions] = useState([]);
  const [duration, setDuration] = useState(0);
  const [timeSinceLast, setTimeSinceLast] = useState(null);
  const [loading, setLoading] = useState(false);
  const { modal, openModal, closeModal } = useFeedbackModal();

  const loadContractions = useCallback(async () => {
    try {
      const data = await api.getContractions(userId);
      setContractions(data);
      
      if (data.length > 0 && data[0].contractionEnd) {
        const lastEnd = new Date(data[0].contractionEnd).getTime();
        const now = Date.now();
        const secondsSince = Math.floor((now - lastEnd) / 1000);
        setTimeSinceLast(secondsSince);
      }
    } catch (error) {
      console.error('Error loading contractions:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadContractions();
    }
  }, [userId, loadContractions]);

  useEffect(() => {
    let interval = null;
    if (activeTimer) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - new Date(activeTimer.contractionStart).getTime()) / 1000);
        setDuration(elapsed);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimer]);

  useEffect(() => {
    let interval = null;
    if (timeSinceLast !== null && !activeTimer) {
      interval = setInterval(() => {
        if (contractions.length > 0 && contractions[0].contractionEnd) {
          const lastEnd = new Date(contractions[0].contractionEnd).getTime();
          const now = Date.now();
          const secondsSince = Math.floor((now - lastEnd) / 1000);
          setTimeSinceLast(secondsSince);
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeSinceLast, activeTimer, contractions]);


  const startContraction = async () => {
    try {
      setLoading(true);
      const timer = await api.startContraction(userId);
      setActiveTimer(timer);
      setDuration(0);
    } catch (error) {
      console.error('Error starting contraction:', error);
      openModal({
        title: 'Start failed',
        message: 'Failed to start contraction timer.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const endContraction = async () => {
    if (!activeTimer) return;
    
    try {
      setLoading(true);
      await api.endContraction(activeTimer.id, userId);
      setActiveTimer(null);
      setDuration(0);
      loadContractions();
    } catch (error) {
      console.error('Error ending contraction:', error);
      openModal({
        title: 'End failed',
        message: 'Failed to end contraction.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getContractionStatus = () => {
    if (contractions.length < 2) return null;
    
    const recent = contractions.slice(0, 3);
    const intervals = [];
    for (let i = 0; i < recent.length - 1; i++) {
      if (recent[i].contractionEnd && recent[i + 1].contractionEnd) {
        const interval = Math.floor(
          (new Date(recent[i].contractionEnd).getTime() - 
          new Date(recent[i + 1].contractionEnd).getTime()
        ) / 1000 / 60);
        intervals.push(Math.abs(interval));
      }
    }
    
    if (intervals.length === 0) return null;
    
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    
    if (avgInterval < 5) return { status: 'ACTIVE', message: 'Active labor - go to hospital!', color: 'error' };
    if (avgInterval < 10) return { status: 'REGULAR', message: 'Regular contractions - contact your doctor', color: 'warning' };
    return { status: 'EARLY', message: 'Early labor - monitor closely', color: 'info' };
  };

  const status = getContractionStatus();

  return (
    <div className="contraction-timer-modern">
      <div className="timer-header-modern">
        <div className="header-content">
          <div className="header-icon-large">⏱️</div>
          <div>
            <h2 className="timer-title">Contraction Timer</h2>
            <p className="timer-subtitle">Track your labor progress</p>
          </div>
        </div>
      </div>

      {status && (
        <div className={`status-alert-modern status-${status.color}`}>
          <div className="status-icon-large">
            {status.color === 'error' ? '🚨' : status.color === 'warning' ? '⚠️' : 'ℹ️'}
          </div>
          <div className="status-content">
            <strong className="status-title">{status.message}</strong>
            <p className="status-subtitle">Average interval: {Math.round(
              contractions.slice(0, 3).reduce((sum, c, i, arr) => {
                if (i === 0 || !c.contractionEnd || !arr[i-1]?.contractionEnd) return sum;
                const interval = Math.abs((new Date(c.contractionEnd) - new Date(arr[i-1].contractionEnd)) / 60000);
                return sum + interval;
              }, 0) / (contractions.length - 1)
            )} minutes</p>
          </div>
        </div>
      )}

      {!activeTimer ? (
        <div className="timer-start-modern">
          <div className="start-card-modern">
            <div className="start-icon">🎯</div>
            <h3>Start Timing</h3>
            <p>Time your contractions to track labor progress. Start when a contraction begins.</p>
            <button 
              className="btn-modern btn-modern-primary start-timer-btn"
              onClick={startContraction} 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  <span>Starting...</span>
                </>
              ) : (
                <>
                  <span>🎯</span>
                  <span>Start Contraction</span>
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="timer-active-modern">
          <div className="duration-display-modern">
            <div className="duration-value">{formatTime(duration)}</div>
            <div className="duration-label">Current Contraction</div>
          </div>
          
          <button 
            className="btn-modern btn-modern-secondary end-contraction-btn-modern"
            onClick={endContraction} 
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="button-spinner"></span>
                <span>Ending...</span>
              </>
            ) : (
              <>
                <span>✅</span>
                <span>End Contraction</span>
              </>
            )}
          </button>
        </div>
      )}

      {timeSinceLast !== null && !activeTimer && (
        <div className="time-since-last-modern">
          <div className="time-label">Time since last contraction</div>
          <div className="time-value">{formatTime(timeSinceLast)}</div>
        </div>
      )}

      {contractions.length > 0 && (
        <div className="contraction-history-modern">
          <div className="history-header">
            <h3>📊 Recent Contractions</h3>
            <span className="history-count">{contractions.length} recorded</span>
          </div>
          <div className="contractions-grid-modern">
            {contractions.slice(0, 5).map((contraction, index) => (
              <div key={contraction.id} className="contraction-card-modern">
                <div className="contraction-number-modern">#{contractions.length - index}</div>
                <div className="contraction-details-modern">
                  {contraction.durationSeconds && (
                    <div className="detail-item">
                      <span className="detail-icon">⏱️</span>
                      <span className="detail-label">Duration:</span>
                      <span className="detail-value">{formatTime(contraction.durationSeconds)}</span>
                    </div>
                  )}
                  {contraction.timeSinceLastSeconds && (
                    <div className="detail-item">
                      <span className="detail-icon">⏳</span>
                      <span className="detail-label">Interval:</span>
                      <span className="detail-value">{formatTime(contraction.timeSinceLastSeconds)}</span>
                    </div>
                  )}
                  <div className="contraction-time-modern">
                    {new Date(contraction.sessionDate).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default ContractionTimer;
